import { ComponentType, ReactElement } from 'react';

import type { EntityApprovalStatus } from '@/api/entity/types';
import InfoIcon from '@/assets/svg/ui-elements/info-circle-filled-orange.svg';
import type { BadgeColor } from '@/design-system';
import { isOlderThanMonths } from '@/utils/date';

import { Confirmation, FirstStep, FourthStep, SecondStep, ThirdStep } from './modules/confirmation';
import { CreateYourEntity } from './modules/create-your-entity';
import { DefineShareStructure } from './modules/define-share-structure';
import { ListDirectors } from './modules/list-directors';
import { Verification } from './modules/verification';

export const sidebarProgressionTitles = [
  { title: 'Entity details', step: 1 },
  { title: 'Directors', step: 2 },
  { title: 'Define Share Structure', step: 3 },
  { title: 'Verification', step: 4 },
  { title: 'Review', step: 5 },
];

export const confirmationTitles = [
  { title: 'Entity details', step: 1 },
  { title: 'Add directors', step: 2 },
  { title: 'Define Share Structure', step: 3 },
  { title: 'Verification', step: 4 },
];

export const entityProgressSteps = [
  { id: 'create-account', name: 'Entity details' },
  { id: 'users', name: 'Add directors' },
  { id: 'share-structure', name: 'Define Share Structure' },
  { id: 'verification', name: 'Verification' },
  { id: 'confirmation', name: 'Review' },
];

export type WizardStepConfig = {
  step: number;
  title: string;
  component: ComponentType;
  description?: string;
  ownsReviewReadOnly?: boolean;
};

export const createAccountSteps: WizardStepConfig[] = [
  {
    step: 1,
    title: 'Entity details',
    component: CreateYourEntity,
    description: `A brief summary of the fund's investment strategy, goals, and objectives. This should provide an overview of the fund's investment approach and what investors can expect.`,
  },
  {
    step: 2,
    title: 'Directors',
    component: ListDirectors,
    description:
      'Here you can add directors, managers and other key roles for the entity, as legally mandated. At least one director field needs to be completed before you can advance to the next section.',
  },
  {
    step: 3,
    title: 'Define Share Structure',
    component: DefineShareStructure,
    description: `Define the share structure of your organization. In this section, you will:
<ul>
<li>Select the relevant share classes.</li>
<li>Indicate the total number of shares per class.</li>
<li>Identify shareholders (both individuals and entities) and their respective number of shares.</li>
<li>List all of your voting shareholders who own 10% or more.</li>
</ul>`,
  },
  {
    step: 4,
    title: 'Verification',
    component: Verification,
    description:
      'To ensure the accuracy and validity of the information provided, please submit supporting documentation or official government website links (or both) as verification. You can include as many documents as needed from the menu below.',
    ownsReviewReadOnly: true,
  },
  { step: 5, title: 'Review', component: Confirmation },
];

export const entityOverviewData = [
  { title: 'Entity details', step: 1, component: FirstStep },
  { title: 'Add directors', step: 2, component: SecondStep },
  { title: 'Define Share Structure', step: 3, component: ThirdStep },
  { title: 'Verification', step: 4, component: FourthStep },
];

export const reviewNoticeTitle =
  'Please review the information you have provided to ensure it is accurate and truthful.';

export const reviewNoticeIntro = 'By submitting this information, you confirm that:';

export const reviewNoticeConfirmations = [
  'The information provided is complete and accurate to the best of your knowledge.',
  'You have the authority to provide this information on behalf of the organization.',
  'You understand that providing false or misleading information may result in consequences, including but not limited to, rejection of your application or termination of your account.',
];

export const legalEntityTypeOptions = [
  { label: 'Limited company', value: 'LimitedCompany' },
  { label: 'Partnership', value: 'Partnership' },
  { label: 'Trust', value: 'Trust' },
  { label: 'Foundation', value: 'Foundation' },
];

export const REGISTRY_SOURCED_TOOLTIP = 'Information sourced from public registry.';

export const DIRECTOR_MINIMUM_MET = 'Minimum requirement met: 1 director added.';

export const DIRECTORS_EMPTY_TITLE = 'No directors yet';

export const DIRECTORS_EMPTY_DESCRIPTION = 'You don’t have any directors yet.';

type DirectorStatusKey =
  | 'Verified'
  | 'Pending'
  | 'waiting-for-approval'
  | 'Created'
  | 'verificationneeded';

const INVITATION_PENDING_TOOLTIP =
  'This user needs to register with Gamma Prime before they can be added. We have sent them an invitation email.';

export const directorStatusBadges: Record<
  DirectorStatusKey,
  { label: string; color: BadgeColor; info?: string }
> = {
  Verified: { label: 'Accepted', color: 'primary' },
  Pending: { label: 'Pending', color: 'yellow', info: INVITATION_PENDING_TOOLTIP },
  Created: { label: 'Pending', color: 'yellow', info: INVITATION_PENDING_TOOLTIP },
  'waiting-for-approval': {
    label: 'Waiting for Approval',
    color: 'orange',
    info: 'This Gamma Prime user has been notified. Their information will be filled out once they approve the request.',
  },
  verificationneeded: {
    label: 'Accepted',
    color: 'primary',
    info: 'This user needs to verify their identity before they can be added. We have sent them an invitation email.',
  },
};

export const ENTITY_STATUS_BADGES: Record<
  EntityApprovalStatus,
  { label: string; color: BadgeColor; info: string }
> = {
  Draft: {
    label: 'Draft',
    color: 'secondary',
    info: 'This is a draft which has not yet been submitted for approval.',
  },
  Verified: {
    label: 'Verified',
    color: 'primary',
    info: 'This legal entity has been approved by Gamma Prime.',
  },
  AwaitingApproval: {
    label: 'Awaiting Approval',
    color: 'yellow',
    info: 'This legal entity has been newly created and is waiting for approval from Gamma Prime.',
  },
  ReVerificationPending: {
    label: 'Re-verification Pending',
    color: 'orange',
    info: 'This legal entity was previously verified, and it is currently waiting for a new approval from Gamma Prime after key information was edited.',
  },
  Blocked: {
    label: 'Blocked',
    color: 'destructive',
    info: 'This legal entity has been blocked by Gamma Prime.',
  },
  Rejected: {
    label: 'Rejected',
    color: 'destructive',
    info: 'This legal entity was rejected by Gamma Prime.',
  },
};

export const registryBannerCopy = {
  idle: {
    title: 'Company registry',
    description:
      "Enter your country, entity name, and entity ID to check if your company is listed in a public registry. If found, we'll auto-fill the form for you (where applicable).",
  },
  detected: {
    title: 'Company registry detected',
    description: 'Auto-fill company data from public registry',
    action: 'Connect Database',
  },
  loaded: {
    title: 'Entity data loaded from public registry.',
    description: 'Locked fields are pre-filled from the official public registry.',
  },
  notFound: {
    title: 'No entity found in public registry.',
    description: 'Please fill in the details manually.',
  },
  error: {
    title: "Couldn't check the public registry.",
    description: 'Please fill in the details manually or try again later.',
  },
} as const;

export const REGISTRY_CHECK_HINT =
  'Enter your country, entity name, and entity ID first so we can check if your company is listed in a public registry.';

type FieldConfig = {
  name: string;
  label: string;
  required?: boolean;
  placeholder: string;
  component: 'text' | 'select' | 'date';
  options?: { value: string; label: string }[];
  errorText?: string;
  column: 'left' | 'right';
  tooltip?: string;
  tooltipIcon?: ReactElement;
};

export const createYourEntityLabels: FieldConfig[] = [
  {
    name: 'legalName',
    label: 'Legal entity name',
    placeholder: 'Enter the registered legal name',
    required: true,
    component: 'text',
    column: 'left',
    errorText:
      ' An entity with this legal name already exists in Gamma Prime. If it was not entered by you, please get in touch with the Gamma Prime team.',
  },
  {
    name: 'displayName',
    label: 'Short entity name',
    placeholder: 'Enter a short or trading name',
    required: true,
    component: 'text',
    column: 'right',
    tooltip:
      'A shorter name for the organization that will be used for presentation purposes within our product (e.g., dashboard, reports).',
    errorText:
      'An entity with this short name already exists in Gamma Prime. Please choose a different short entity name.',
  },
  {
    name: 'country',
    label: 'Country / jurisdiction',
    placeholder: 'Select country or jurisdiction',
    required: true,
    component: 'select',
    column: 'left',
  },
  {
    name: 'state',
    label: 'State / region',
    placeholder: 'Select state or region',
    required: false,
    component: 'select',
    column: 'right',
  },
  {
    name: 'registeredAt',
    label: 'Date of registration',
    placeholder: 'Select registration date',
    required: true,
    component: 'date',
    column: 'left',
  },
  {
    name: 'identificationNumber',
    label: 'Entity Identification Number',
    placeholder: 'Enter registration number',
    required: true,
    component: 'text',
    column: 'right',
  },
  {
    name: 'entityEmail',
    label: 'Entity email',
    placeholder: 'Enter company email address',
    required: true,
    component: 'text',
    tooltip: 'This email must be unique. It can’t be assigned to any other user or Entity member.',
    tooltipIcon: <InfoIcon />,
    column: 'left',
  },
  {
    name: 'type',
    label: 'Legal entity type',
    placeholder: 'Select legal entity type',
    required: true,
    options: legalEntityTypeOptions,
    component: 'select',
    column: 'right',
  },
  {
    name: 'entityWebsite',
    label: 'Website',
    placeholder: 'Enter website URL',
    required: false,
    component: 'text',
    column: 'left',
    errorText: 'Please enter a valid website URL',
  },
];

export const registeredAddressLabels: FieldConfig[] = [
  {
    name: 'registeredAddressCity',
    label: 'City',
    placeholder: 'Enter city',
    required: true,
    component: 'text',
    column: 'left',
  },
  {
    name: 'registeredAddress',
    label: 'Address',
    placeholder: 'Enter registered street address',
    required: true,
    component: 'text',
    column: 'right',
  },
  {
    name: 'registeredAddressPostalCode',
    label: 'Postal code',
    placeholder: 'Enter postal code',
    required: true,
    component: 'text',
    column: 'left',
  },
];

export const defineShareStructureLabels = [
  {
    name: 'fullName',
    label: 'Full Name',
    placeholder: 'Shareholder Name',
  },
  {
    name: 'email',
    type: 'email',
    label: 'Email address',
    placeholder: 'Shareholder Email',
  },
  {
    name: 'numberOfShares',
    label: 'Number of Shares',
    placeholder: '# Shares',
  },
];

export type VerificationType =
  | 'RegisterOfDirectors'
  | 'RegisterOfShareholders'
  | 'CertificateOfRegistration'
  | 'ProofOfAddress'
  | 'ArticlesOrEquivalent'
  | 'CertificateOfGoodStanding'
  | 'CertificateOfIncumbency';

export const titleToTypeMap: Record<string, VerificationType> = {
  'Register of directors': 'RegisterOfDirectors',
  'Register of shareholders': 'RegisterOfShareholders',
  'Certificate of registration': 'CertificateOfRegistration',
  'Proof of address': 'ProofOfAddress',
  'Articles or equivalent': 'ArticlesOrEquivalent',
  'Certificate of good standing': 'CertificateOfGoodStanding',
  'Certificate of incumbency': 'CertificateOfIncumbency',
};

export const typeToTitleMap = Object.entries(titleToTypeMap).reduce(
  (acc, [title, type]) => {
    acc[type] = title;
    return acc;
  },
  {} as Record<VerificationType, string>,
);

export type VerificationSection = {
  title: string;
  required: boolean;
};

/**
 * A Certificate of Good Standing is required for entities formed more than
 * 12 months ago (newly formed entities are exempt).
 */
export const GOOD_STANDING_REQUIRED_AFTER_MONTHS = 12;

const baseUploadSections: VerificationSection[] = [
  { title: 'Register of directors', required: true },
  { title: 'Register of shareholders', required: true },
  { title: 'Certificate of registration', required: true },
  { title: 'Proof of address', required: true },
];

export const getRequiredUploadSections = (
  registeredAt: string | Date | null | undefined,
): VerificationSection[] =>
  isOlderThanMonths(registeredAt, GOOD_STANDING_REQUIRED_AFTER_MONTHS)
    ? [...baseUploadSections, { title: 'Certificate of good standing', required: true }]
    : baseUploadSections;

export const shareholderOptions = [
  { label: 'Individual', value: 'Individual' },
  { label: 'Entity', value: 'Entity' },
] as const;

export const selectClassOptions = [
  { label: 'Preferred Shares', value: 'Preferred' },
  { label: 'Ordinary Shares', value: 'Ordinary' },
  { label: 'Class A Shares', value: 'ClassA' },
  { label: 'Class B Shares', value: 'ClassB' },
  { label: 'Class C Shares', value: 'ClassC' },
  { label: 'Limited Partnership Units', value: 'LimitedPartnershipUnits' },
  { label: 'General Partner Units', value: 'GeneralPartnerUnits' },
];

export const shareClassTableHeaders = [
  { name: 'type', label: 'Type' },
  { name: 'fullName', label: 'Full Name' },
  { name: 'email', label: 'Email address' },
  { name: 'numberOfShares', label: 'Number of Shares' },
];

const STEP_1_DIFF_PREFIXES = [
  'legalName',
  'displayName',
  'identificationNumber',
  'country',
  'state',
  'type',
  'registeredAt',
  'entityEmail',
  'entityWebsite',
  'registeredAddressCity',
  'registeredAddress',
  'registeredAddressPostalCode',
  'logoImageId',
];

const STEP_2_DIFF_PREFIXES = ['members', 'invitations'];
const STEP_3_DIFF_PREFIXES = ['shareStructure'];
const STEP_4_DIFF_PREFIXES = ['verifications'];

export const REVIEW_STEP_PREFIXES: Record<number, string[]> = {
  1: STEP_1_DIFF_PREFIXES,
  2: STEP_2_DIFF_PREFIXES,
  3: STEP_3_DIFF_PREFIXES,
  4: STEP_4_DIFF_PREFIXES,
  5: [
    ...STEP_1_DIFF_PREFIXES,
    ...STEP_2_DIFF_PREFIXES,
    ...STEP_3_DIFF_PREFIXES,
    ...STEP_4_DIFF_PREFIXES,
  ],
};

export const UPDATED_BADGE_LABEL = 'Updated';
